import { useState } from 'react';
import { Link } from 'react-router-dom';
import { HiCheckCircle } from 'react-icons/hi';
import { waLink, STORE_WHATSAPP } from '../utils/whatsapp';
import '../styles/Policy.css';

const STORE_EMAIL = 'celestialstones33@gmail.com';

const KINDS = [
  {
    id: 'delete',
    label: 'Delete my data',
    blurb: 'Erase my account, orders and personal details.',
  },
  {
    id: 'copy',
    label: 'Send me a copy',
    blurb: 'A copy of everything you hold about me.',
  },
  {
    id: 'correct',
    label: 'Correct my data',
    blurb: "Something you hold about me is wrong.",
  },
];

/*
  The DPDP Act gives people the right to have their data erased,
  corrected, or handed back to them. There was no way to ask for any of
  that — so this builds the request and sends it by email or WhatsApp,
  whichever they prefer.

  Deliberately NOT a database write. An unauthenticated form that
  deleted rows would be a way for anyone to erase someone else's
  account by typing their email. A person reads these and checks who is
  asking, which is what the law expects anyway.
*/
export default function DataRequest() {
  const [kind, setKind] = useState('delete');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [detail, setDetail] = useState('');
  const [error, setError] = useState('');
  const [sentVia, setSentVia] = useState(null);

  const chosen = KINDS.find((k) => k.id === kind);

  const validate = () => {
    if (!email.trim() && !phone.trim()) {
      setError(
        'Please give the email address or phone number on your order, so we ' +
          'can find your records.'
      );
      return false;
    }

    if (email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      setError('That email address doesn’t look right.');
      return false;
    }

    setError('');
    return true;
  };

  const body = () =>
    [
      `Request type: ${chosen.label}`,
      '',
      email.trim() ? `Email on file: ${email.trim()}` : null,
      phone.trim() ? `Phone on file: ${phone.trim()}` : null,
      '',
      detail.trim() ? `Details: ${detail.trim()}` : null,
      '',
      'Sent from the Data & Privacy Requests page on celestialstones.in',
    ]
      .filter((line) => line !== null)
      .join('\n');

  const sendEmail = () => {
    if (!validate()) return;

    window.location.href = `mailto:${STORE_EMAIL}?subject=${encodeURIComponent(
      `Data request: ${chosen.label}`
    )}&body=${encodeURIComponent(body())}`;

    setSentVia('email');
  };

  const sendWhatsApp = () => {
    if (!validate()) return;

    window.open(
      waLink(STORE_WHATSAPP, `Hello, I have a data request.\n\n${body()}`),
      '_blank'
    );

    setSentVia('whatsapp');
  };

  return (
    <section className="policy-page">
      <div className="policy-container">
        <header className="policy-head">
          <h1>Data &amp; Privacy Requests</h1>
          <p className="policy-updated">
            Your rights under India's Digital Personal Data Protection Act
          </p>
        </header>

        <div className="policy-body">
          <p className="policy-lead">
            You can ask us to delete the personal data we hold about you, send
            you a copy of it, or correct anything that's wrong. Tell us which
            below and we'll reply within 30 days.
          </p>

          {sentVia ? (
            <div className="policy-done">
              <HiCheckCircle />
              <h2>Your request is ready to send</h2>
              <p>
                {sentVia === 'email'
                  ? `We've opened your email app with the request addressed to ${STORE_EMAIL}. It isn't sent until you press send there.`
                  : "We've opened WhatsApp with your request written out. It isn't sent until you press send there."}
              </p>
              <p className="policy-note">
                Once it reaches us we'll confirm your identity against the
                order details you gave, then action it within 30 days.
              </p>
              <button className="policy-btn" onClick={() => setSentVia(null)}>
                Make another request
              </button>
            </div>
          ) : (
            <>
              <h2>What would you like us to do?</h2>

              <div className="request-kinds">
                {KINDS.map((option) => (
                  <label
                    key={option.id}
                    className={`request-kind ${kind === option.id ? 'is-on' : ''}`}
                  >
                    <input
                      type="radio"
                      name="kind"
                      value={option.id}
                      checked={kind === option.id}
                      onChange={() => setKind(option.id)}
                    />
                    <span>
                      <strong>{option.label}</strong>
                      {option.blurb}
                    </span>
                  </label>
                ))}
              </div>

              <h2>How do we find you?</h2>
              <p className="policy-note">
                Give at least one of these — whichever you used when ordering.
              </p>

              <div className="policy-form">
                <label>
                  Email address
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    autoComplete="email"
                  />
                </label>

                <label>
                  Phone / WhatsApp number
                  <input
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="+91 99999 00000"
                    autoComplete="tel"
                  />
                </label>

                <label>
                  Anything else we should know <span>(optional)</span>
                  <textarea
                    rows={4}
                    value={detail}
                    onChange={(e) => setDetail(e.target.value)}
                    placeholder="e.g. which order this relates to"
                  />
                </label>

                {error && (
                  <p className="policy-error" role="alert">
                    {error}
                  </p>
                )}

                <div className="policy-actions">
                  <button className="policy-btn" onClick={sendEmail}>
                    Send by email
                  </button>
                  <button className="policy-btn is-ghost" onClick={sendWhatsApp}>
                    Send on WhatsApp
                  </button>
                </div>
              </div>
            </>
          )}

          <h2>What deletion actually means</h2>
          <p>
            We erase your account, saved address, and the personal details on
            your orders. We have to keep the bare invoice record — amount, date
            and order number — because tax law requires it; that record no
            longer identifies you once your details are removed.
          </p>

          <h2>If you have an account</h2>
          <p>
            You can edit your own details any time under{' '}
            <Link to="/account/profile">My Account</Link> without asking us.
          </p>

          <p>
            See also our <Link to="/privacy-policy">Privacy Policy</Link> and{' '}
            <Link to="/cookie-policy">Cookie Policy</Link>.
          </p>
        </div>
      </div>
    </section>
  );
}
